import {Fragment, useCallback, useEffect, useId, useMemo, useRef} from 'react'
import type {ReactElement, PropsWithChildren, KeyboardEvent, FocusEvent} from 'react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {noop} from '@github-ui/noop'
import {Box, merge} from '@primer/react'
import {clsx} from 'clsx'

import {ActionsProvider} from './ActionsContext'
import {MetadataContainer, type MetadataContainerProps} from './MetadataContainer'
import {useListViewId} from '../ListView/IdContext'
import {useListViewVariant} from '../ListView/VariantContext'
import {useListViewItems} from '../ListView/ItemsContext'
import {useListViewSelection} from '../ListView/SelectionContext'
import {SelectionProvider, useListItemSelection} from './SelectionContext'
import {TitleProvider, useListItemTitle} from './TitleContext'
import {DescriptionProvider, useListItemDescription} from './DescriptionContext'
import {StatusProvider, useListItemStatus} from './StatusContext'
import {ActionBar} from './ActionBar'
import type {Title} from './Title'
import {LeadingBadgeProvider, useListItemLeadingBadge} from './LeadingBadgeContext'
import {TrailingBadgeProvider, useListItemTrailingBadge} from './TrailingBadgeContext'
import {NewActivityProvider, useListItemNewActivity} from './NewActivityContext'

export type ListItemProps = PropsWithChildren<{
  isSelected?: boolean
  isActive?: boolean
  onSelect?: (isSelected: boolean) => void
  /*
   * Optional. Overrides the default behavior of the list item when a key is pressed
   */
  onKeyDown?: (event: KeyboardEvent<HTMLLIElement>) => void
  onFocus?: (event: FocusEvent<HTMLLIElement>) => void

  /**
   * A ListItem title communicates the overall purpose of the ListItem.
   */
  title: ReactElement<typeof Title>

  /**
   * Optional extra elements to display on the right side of the list item. You can optionally wrap individual
   * pieces of content in `ListItem.Metadata` for a consistent appearance.
   */
  metadata?: MetadataContainerProps['children']

  /**
   * Optional menu of additional actions to be shown on the right side of the ListItem. Use `ListItem.ActionBar`.
   */
  secondaryActions?: ReactElement<typeof ActionBar>
  sx?: BetterSystemStyleObject
  metadataContainerSx?: BetterSystemStyleObject
}>

// by default merge (via deepmerge) combines arrays by concatenating them
// this creates unexpected behavior when merging sx props (such as display)
// this util allows us more fine-grained control over how arrays are merged
const preferIncomingArray = (destinationArray: unknown[], sourceArray: unknown[]) =>
  sourceArray.length > 0 ? sourceArray : destinationArray

// minmax(0, max-content) - '0' is required to fix overflow caused by the "metadata" on smaller screen sizes
// '1fr' is required to fill available space ('auto' will cause content to overflow)
const commonDefaultGridTemplateColumns = 'min-content min-content minmax(30%, 1fr) minmax(0, max-content)'
// 2em for short titles and description to be aligned next to title
const commonCompactGridTemplateColumns =
  'min-content min-content minmax(2em, max-content) minmax(0, auto) minmax(min-content, max-content)'
const commonListItemSx: BetterSystemStyleObject = {
  position: 'relative',
  display: 'grid',
  minHeight: '2rem' /* 32px */,
  fontSize: 1,
  gap: 1,
  color: 'fg.default',
  // this is to mimic what subgrid will do for us, which is
  // to create a placeholder for metadata items that might still be empty (but hide them on mobile if they are empty)
  '.listitem-metadata:empty': {
    display: ['none', 'flex'],
  },
  ':not(:last-child)': {
    borderBottom: '1px solid',
    borderColor: 'border.subtle',
  },

  '&.listviewitem-compact': {
    minHeight: '1rem',
  },

  '&:hover': {
    backgroundColor: 'canvas.subtle',
  },

  '&.listviewitem-selected': {
    backgroundColor: 'accent.subtle',
    boxShadow: '0 0 0 1px var(--borderColor-accent-emphasis, var(--color-accent-fg))',
    zIndex: 1,

    '&:hover': {
      backgroundColor: 'accent.subtle',
    },
  },

  '&:focus-visible': {
    outline: '2px solid var(--focus-outlineColor, var(--color-accent-fg))',
    outlineOffset: '-2px',
    boxShadow: 'none',
  },
}

const defaultTwoRow = '"selection status primary metadata actions" "selection status main-content metadata actions"'
const defaultThreeRow =
  '"selection status primary actions" "selection status metadata actions" "selection status main-content actions"'
const compactTwoRow = '"selection status primary main-content actions" "selection status metadata metadata actions"'
const compactOneRow = '"selection status primary main-content metadata actions"'

const defaultVariantListItemSx: BetterSystemStyleObject = {
  // xsmall: 3 rows, small+: 2 rows
  // https://primer.style/foundations/layout#breakpoints
  gridTemplateRows: ['repeat(3,auto)', 'repeat(2, auto)'],
  gridTemplateAreas: [defaultThreeRow, defaultTwoRow],
  gridTemplateColumns: `${commonDefaultGridTemplateColumns} min-content`,
}

const compactVariantListItemSx: BetterSystemStyleObject = {
  // xsmall: 2 rows, small+: 1 row
  // https://primer.style/foundations/layout#breakpoints
  gridTemplateRows: ['repeat(2,auto)', 'auto'],
  gridTemplateAreas: [compactTwoRow, compactOneRow],
  gridTemplateColumns: `${commonCompactGridTemplateColumns} min-content`,
}

const ListItemBase = ({
  children,
  isActive = false,
  title,
  metadata,
  secondaryActions,
  sx: externalListItemSx,
  metadataContainerSx,
  ...props
}: Omit<ListItemProps, 'isSelected' | 'onSelect'>): JSX.Element => {
  const {idPrefix} = useListViewId()
  const {isSelectable} = useListViewSelection()
  const {variant} = useListViewVariant()
  const {anyItemsWithActionBar, hasResizableActionsWithActionBar} = useListViewItems()
  const uniqueIdSuffix = useId()
  const {isSelected, onSelect} = useListItemSelection()
  const {status: labelStatus} = useListItemStatus()
  const {title: labelTitle, titleAction} = useListItemTitle()
  const {description: labelDescription} = useListItemDescription()
  const {leadingBadge: labelLeadingBadge} = useListItemLeadingBadge()
  const {trailingBadge: labelTrailingBadge} = useListItemTrailingBadge()
  const {hasNewActivity} = useListItemNewActivity()
  const itemRef = useRef<HTMLLIElement>(null)

  // This allows the actions to show in the action bar depending on the screen size. These actions
  // will move into a dropdown menu when the screen isn't big enough to fit them side by side.
  // Edge case: if some items have resizable actions and some don't have an actionbar,
  // column alignment will be off.
  const gridTemplateSx =
    anyItemsWithActionBar && hasResizableActionsWithActionBar
      ? variant === 'compact'
        ? {gridTemplateColumns: `${commonCompactGridTemplateColumns} minmax(0, auto)`}
        : {gridTemplateColumns: `${commonDefaultGridTemplateColumns} auto`}
      : {}

  const baseListItemSx =
    variant === 'compact'
      ? merge<BetterSystemStyleObject>(commonListItemSx, compactVariantListItemSx)
      : merge<BetterSystemStyleObject>(commonListItemSx, defaultVariantListItemSx)
  const flexibleListItemSx: BetterSystemStyleObject = merge<BetterSystemStyleObject>(baseListItemSx, gridTemplateSx)
  const listItemSx = externalListItemSx
    ? merge<BetterSystemStyleObject>(flexibleListItemSx, externalListItemSx, {arrayMerge: preferIncomingArray})
    : flexibleListItemSx

  useEffect(() => {
    if (!itemRef.current) return
    // Focus the element unless another element in the document is specifically focused
    if (isActive && document.activeElement?.tagName === 'BODY') {
      itemRef.current.focus()
    }
  })

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLLIElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'Enter':
          if (titleAction) titleAction(e)
          break
        case ' ':
          // When the user presses Space
          if (!isSelectable) break
          if (itemRef?.current !== document.activeElement) break // if the focus is inside the listitem, do not select the item
          e.preventDefault()
          onSelect(!isSelected)
          break
        case 'Escape':
          itemRef?.current?.focus()
          break
        default:
          break
      }
    },
    [titleAction, isSelectable, onSelect, isSelected],
  )

  useEffect(() => {}, [metadata, secondaryActions])

  const getAriaLabel = useCallback(() => {
    const labelSelected = isSelected ? 'Selected' : ''
    const labelNewActivity = hasNewActivity ? 'New activity' : ''
    const hasSecondaryActions = !!secondaryActions
    const hasMetadata = (Array.isArray(metadata) && metadata.length > 0) || (metadata && !Array.isArray(metadata))
    let labelAdditionalInfo = ''
    if (hasMetadata || hasSecondaryActions) {
      labelAdditionalInfo = 'More information available below'
    }

    const main = [labelLeadingBadge, labelTitle, labelTrailingBadge, labelStatus]
      .filter(str => str.trim())
      .join(': ')
      .replace(/\.+$/, '') // Remove additional periods
    const ariaLabel = [labelSelected, main, labelDescription, labelNewActivity, labelAdditionalInfo]
      .filter(str => str.trim())
      .join('. ')

    return ariaLabel.endsWith('.') ? ariaLabel : `${ariaLabel}.`
  }, [
    secondaryActions,
    metadata,
    isSelected,
    hasNewActivity,
    labelLeadingBadge,
    labelTitle,
    labelTrailingBadge,
    labelStatus,
    labelDescription,
  ])

  return (
    <Box
      as="li"
      ref={itemRef}
      id={`${idPrefix}-listview-node-${uniqueIdSuffix}`}
      role="listitem"
      className={clsx(
        isSelected && 'listviewitem-selected',
        variant === 'compact' && 'listviewitem-compact',
        'listviewitem',
      )}
      tabIndex={-1} // Handled by useFocusZone with roving tabIndex
      aria-label={getAriaLabel()}
      sx={listItemSx}
      onKeyDown={onKeyDown}
      {...props}
    >
      {title}
      {children}
      {Array.isArray(metadata) && metadata.length > 0 ? (
        <MetadataContainer sx={metadataContainerSx}>
          {metadata.map((metadataItem, index) => (
            <Fragment key={index}>{metadataItem}</Fragment>
          ))}
        </MetadataContainer>
      ) : (
        metadata &&
        !Array.isArray(metadata) && <MetadataContainer sx={metadataContainerSx}>{metadata}</MetadataContainer>
      )}
      {secondaryActions ?? (anyItemsWithActionBar && <ActionBar />)}
    </Box>
  )
}

export const ListItem = ({
  children,
  isSelected = false,
  onSelect = noop,
  ...rest
}: PropsWithChildren<ListItemProps>): JSX.Element => {
  const {setSelectedCount} = useListViewSelection()
  const isSelectedRef = useRef(isSelected)
  isSelectedRef.current = isSelected

  useEffect(() => {
    setSelectedCount(count => (isSelected ? count + 1 : Math.max(0, count - 1)))
  }, [isSelected, setSelectedCount])

  useEffect(() => {
    return () => {
      if (isSelectedRef.current) {
        setSelectedCount(count => Math.max(0, count - 1))
      }
    }
  }, [setSelectedCount])

  const selectionProviderProps = useMemo(() => ({isSelected, onSelect}), [isSelected, onSelect])

  return (
    <ActionsProvider>
      <LeadingBadgeProvider>
        <TitleProvider>
          <SelectionProvider value={selectionProviderProps}>
            <NewActivityProvider>
              <TrailingBadgeProvider>
                <DescriptionProvider>
                  <StatusProvider>
                    <ListItemBase {...rest}>{children}</ListItemBase>
                  </StatusProvider>
                </DescriptionProvider>
              </TrailingBadgeProvider>
            </NewActivityProvider>
          </SelectionProvider>
        </TitleProvider>
      </LeadingBadgeProvider>
    </ActionsProvider>
  )
}

try{ ListItemBase.displayName ||= 'ListItemBase' } catch {}
try{ ListItem.displayName ||= 'ListItem' } catch {}